// src/pages/Sales/SalesDashboard.jsx
// Only change from original: conditional salesService import via USE_MOCK flag.
// Every component, hook, and prop is identical.

import React, { useState, useEffect } from 'react';
import { usePerformanceFilters } from '../../hooks/useSalesData';
import { Header } from '../../components/sales/Header';
import KPISection from '../../components/sales/sections/KPISection';
import TeamPerformanceSection from '../../components/sales/sections/TeamPerformanceSection';
import ClosedDealsSection from '../../components/sales/sections/ClosedDealsSection';
import CallsStatusSection from '../../components/sales/sections/CallsStatusSection';
import OffersSection from '../../components/sales/sections/OffersSection';
import HeatmapSection from '../../components/sales/sections/HeatmapSection';
import CollapsibleSection from '../../components/common/CollapsibleSection';
import PerformanceFilters from '../../components/sales/sections/PerformanceFilters';
import { LoadingSpinner, ErrorMessage, NoDataMessage } from '../../components/common';

// ── Mock toggle ───────────────────────────────────────────────────────────────
// Set REACT_APP_USE_MOCK_API=true in .env and restart to use mock data.
// Patch must be synchronous so useFilterOptions (called by KPISection on
// first render) already sees the mock methods — async patching causes a
// race condition where the real service fires first.

import realSalesService from '../../services/salesService';
import mockSalesService from '../../services/mockSalesService';

const USE_MOCK = process.env.REACT_APP_USE_MOCK_API === 'true';

if (USE_MOCK) {
  // Synchronously replace every method on the singleton so ALL components
  // that import salesService (KPISection, TeamPerformanceSection,
  // HeatmapSection, useFilterOptions) hit the mock from the first render.
  Object.keys(mockSalesService).forEach((key) => {
    realSalesService[key] = mockSalesService[key];
  });
}

// ─────────────────────────────────────────────────────────────────────────────

const SalesDashboard = () => {

  const {
    kpis, performanceData, performanceLoading, dataInfo,
    loading, refreshing, error, filters, filterOptions,
    lastUpdated, setPerformanceFilters, refresh, getCacheInfo,
  } = usePerformanceFilters({ date_range: 'last_4_weeks', closer: 'all', offer_type: 'all' });

  const [kpiStyleFilters, setKpiStyleFilters] = useState({
    aggregation_level: 'weekly',
    reference_date: new Date().toISOString().split('T')[0],
    periods_back: 12,
    closer: 'all',
    offer_type: 'all',
  });

  const [performanceFilters, setPerformanceFiltersState] = useState({
    aggregation_level: 'weekly',
    reference_date: new Date().toISOString().split('T')[0],
    periods_back: 12,
    closer: 'all',
    offer_type: 'all',
  });

  const [kpiCollapsed,         setKpiCollapsed]         = useState(false);
  const [performanceCollapsed, setPerformanceCollapsed] = useState(false);
  const [heatmapCollapsed,     setHeatmapCollapsed]     = useState(false);

  // ── Filter converters (unchanged from original) ───────────────────────────

  const convertKpiFiltersToHookFilters = (kpiFilters) => {
    try {
      let refDate = kpiFilters.reference_date ? new Date(kpiFilters.reference_date) : new Date();
      if (isNaN(refDate)) refDate = new Date();
      const periodDays = kpiFilters.aggregation_level === 'weekly' ? 7 : 30;
      const periodsBack = kpiFilters.periods_back === 'all' ? 12 : (parseInt(kpiFilters.periods_back) || 12);
      const startDate = new Date(refDate - periodsBack * periodDays * 864e5);
      return {
        date_filter_type: 'range',
        start_date: startDate.toISOString().split('T')[0],
        end_date:   refDate.toISOString().split('T')[0],
        closer:     kpiFilters.closer || 'all',
        offer_type: kpiFilters.offer_type || 'all',
      };
    } catch {
      const today = new Date();
      const past  = new Date(); past.setMonth(today.getMonth() - 3);
      return { date_filter_type: 'range', start_date: past.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0], closer: 'all', offer_type: 'all' };
    }
  };

  const convertHookFiltersToKpiFilters = (hookFilters) => ({
    ...kpiStyleFilters,
    closer:     hookFilters.closer     || 'all',
    offer_type: hookFilters.offer_type || 'all',
  });

  const handleKpiFiltersChange = (newKpiFilters) => {
    setKpiStyleFilters(newKpiFilters);
    setPerformanceFilters(convertKpiFiltersToHookFilters(newKpiFilters));
  };

  const handlePerformanceFiltersChange = (newFilters) => {
    setPerformanceFiltersState(newFilters);
    setKpiStyleFilters(newFilters);
    setPerformanceFilters(convertKpiFiltersToHookFilters(newFilters));
  };

  const handleTeamPerformanceFiltersChange = (newFilters) => {
    setKpiStyleFilters(newFilters);
    setPerformanceFiltersState(newFilters);
    setPerformanceFilters(convertKpiFiltersToHookFilters(newFilters));
  };

  useEffect(() => { setKpiStyleFilters(convertHookFiltersToKpiFilters(filters)); }, [filters]);
  useEffect(() => { document.title = 'Pulse: Sales Performance'; }, []);

  // ── Render guards ─────────────────────────────────────────────────────────

  if (loading && !kpis)  return <LoadingSpinner />;
  if (error   && !kpis)  return <ErrorMessage message={error} onRetry={refresh} />;
  if (!kpis)             return <NoDataMessage onRetry={refresh} />;

  return (
    <div className="min-h-screen bg-grey-50">
      <div className="max-w-full mx-auto space-y-4">

        <Header loading={refreshing} onRefresh={refresh} lastUpdated={lastUpdated} />

        {refreshing && (
          <div className="bg-brightblue-50 border border-brightblue-200 rounded-lg px-4 py-2.5 flex items-center justify-center gap-2">
            <div className="w-3.5 h-3.5 border-2 border-brightblue-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-brightblue-700">Refreshing data…</span>
          </div>
        )}

        {error && kpis && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-yellow-700">⚠️ Error refreshing: {error}</span>
            <button onClick={refresh} className="text-xs text-yellow-700 hover:text-yellow-900 underline">Retry</button>
          </div>
        )}

        {/* KPIs */}
        <CollapsibleSection
          title="Key Performance Indicators"
          subtitle="Sales metrics and conversion summary"
          collapsed={kpiCollapsed}
          setCollapsed={setKpiCollapsed}
        >
          <KPISection
            kpiData={kpis}
            onFiltersChange={handleKpiFiltersChange}
            loading={refreshing}
            availableClosers={filterOptions.closers}
          />
        </CollapsibleSection>

        {/* Performance */}
        <CollapsibleSection
          title="Performance Breakdown"
          subtitle="Detailed view of team output, deal closures, offers made, and call activity"
          collapsed={performanceCollapsed}
          setCollapsed={setPerformanceCollapsed}
        >
          <PerformanceFilters
            filters={performanceFilters}
            onFiltersChange={handlePerformanceFiltersChange}
            loading={performanceLoading}
            dataInfo={dataInfo}
          />

          <div className="relative mt-4">
            {performanceLoading && (
              <div className="absolute inset-0 bg-white/75 flex items-center justify-center z-10 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-brightblue-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-brightblue-700">Updating performance data…</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6 space-y-4">
                <TeamPerformanceSection filters={kpiStyleFilters} onFiltersChange={handleTeamPerformanceFiltersChange} />
                <CallsStatusSection data={performanceData.callStatus} />
              </div>
              <div className="col-span-6 space-y-4">
                <OffersSection data={performanceData.offers} />
                <ClosedDealsSection data={performanceData.teamData} />
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Heatmap */}
        <CollapsibleSection
          title="Heatmap"
          subtitle="Track team metrics week by week"
          collapsed={heatmapCollapsed}
          setCollapsed={setHeatmapCollapsed}
        >
          <HeatmapSection />
        </CollapsibleSection>

        {/* Footer */}
        <div className="mt-4 text-center text-xs text-grey-400 flex justify-center gap-4">
          <span>Last Updated: {lastUpdated ? new Date(lastUpdated).toLocaleString() : 'Never'}</span>
          <span>•</span>
          <span>Cache: {getCacheInfo().isValid ? 'Valid' : 'Expired'}</span>
          <span>•</span>
          <span>Age: {getCacheInfo().cacheAge ? `${Math.round(getCacheInfo().cacheAge / 1000 / 60)}m` : 'N/A'}</span>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;